import * as babylon from "@babylonjs/core";

import { DEFAULT_DATA_SETTINGS } from "@/shared/state/visualization/schema/v1/three-d";

import type * as components from "../../components";
import { BabylonState } from "../../state";

const NS_PER_SECOND = 1e9;

/**
 * How far below `interpolationThresholdHz` a pair of samples may fall and still
 * be blended, since timestamps rounded to whole nanoseconds put a stream
 * recorded at exactly the threshold a hair below it on every other pair.
 */
const INTERPOLATION_THRESHOLD_EPSILON_HZ = 0.001;

/**
 * How a sampler treats a time between two recorded samples: which pairs it
 * blends, and which it holds at the earlier sample (see
 * {@link interpolatesBetween}). The data settings singleton is one, so a system
 * hands it straight to its sampler.
 */
export type Interpolation = Pick<
  components.singletons.DataSettings,
  "interpolationEnabled" | "interpolationThresholdHz"
>;

/**
 * How the panel samples between two recorded samples.
 *
 * Every system that samples a topic's timeline reads the setting through this,
 * once per call, and hands it to its sampler as a plain argument, so the
 * samplers stay pure functions of a cache and a time. It lives here, beside
 * {@link logTimeBinarySearch}, because every one of those systems already
 * imports from this module.
 *
 * The singleton is absent before the first `setState` and in hand-built states,
 * and absence reads as `DEFAULT_DATA_SETTINGS`, which is how the panel sampled
 * before the setting existed. This is the one place that fallback is written.
 */
export function interpolationOf(state: BabylonState): Interpolation {
  return state.sceneConfig.dataSettings ?? DEFAULT_DATA_SETTINGS;
}

/**
 * Whether a sampler blends the successive samples recorded at `earlier` and
 * `later` (epoch nanoseconds), rather than holding the earlier one.
 *
 * Blends only while interpolation is enabled and the pair is at least as
 * frequent as the threshold, to within
 * {@link INTERPOLATION_THRESHOLD_EPSILON_HZ}: `1 / (later - earlier)` seconds
 * at or above `interpolationThresholdHz` less that epsilon. Multiplied out
 * rather than divided, so a threshold of `0` blends every pair with no division
 * by zero. The gap is subtracted as a `bigint` before it becomes a float;
 * converting two epoch times first would round each to the nearest 256 ns.
 */
export function interpolatesBetween(
  interpolation: Interpolation,
  earlier: bigint,
  later: bigint,
): boolean {
  return (
    interpolation.interpolationEnabled &&
    Number(later - earlier) *
      (interpolation.interpolationThresholdHz -
        INTERPOLATION_THRESHOLD_EPSILON_HZ) <=
      NS_PER_SECOND
  );
}

/**
 * Which recorded sample a sampler reads at `time`: the latest one at or before
 * it, and, when `interpolation` blends it with the next (see
 * {@link interpolatesBetween}), how far towards the next one `time` is.
 *
 * Every sampler of a timeline goes through this, so they all agree on which
 * pairs are blended. `fraction` is absent when the sampler should hold
 * `index`: past the last sample, while interpolation is off, across a gap
 * sparser than the threshold, and between two samples sharing a timestamp,
 * where a fraction would be NaN. Before the first sample `index` is 0 and a
 * blend's `fraction` clamps to 0. Undefined for an empty timeline.
 */
export function sampleAt(
  logtimes: bigint[],
  time: bigint,
  interpolation: Interpolation,
): { index: number; sampleTime: bigint; fraction?: number } | undefined {
  const index = logTimeBinarySearch(logtimes, time);
  const sampleTime = logtimes.at(index);
  if (sampleTime === undefined) {
    return undefined;
  }
  const nextTime = logtimes.at(index + 1);
  if (
    nextTime === undefined ||
    nextTime === sampleTime ||
    !interpolatesBetween(interpolation, sampleTime, nextTime)
  ) {
    return { index, sampleTime };
  }
  const fraction = Math.min(
    Math.max(Number(time - sampleTime) / Number(nextTime - sampleTime), 0),
    1,
  );
  return { index, sampleTime, fraction };
}

/**
 * Syncs the data-space position of all entities with the current time.
 *
 * Between two samples each position is blended or held as the panel's data
 * settings say. They are read every frame rather than cached, so a change to
 * them needs no restaling here and takes effect on the next frame, playing or
 * paused.
 *
 * TODO(dave): this is currently logarithmic complexity for all entities.
 * Decouple it from the render loop.
 * Using low/high LODs will also set a cap on the number of points that
 * need to be inspected.
 */
export function updatePositions(state: BabylonState) {
  const time = state.sceneConfig.time;
  if (time === undefined) {
    return;
  }
  const interpolation = interpolationOf(state);
  for (const entity of Object.values(state.entities)) {
    if (!entity.position || entity.position.cache.load.state !== "loaded") {
      continue;
    }
    const cache = entity.position.cache;
    // Figure out what index from the position cache to load.
    // This could possibly be implemented with an amortized linear search instead
    // by using the currently-loaded logtime, but that only
    // works at slow playback speeds, and doesn't work for fast
    // playback or seeking.
    // The logtime binary search will also be reusable for buffering data
    // when we decide to downsample the full path followed by the Entity
    // eg we can downsample to 1hz or so for the full path, and then buffer playback
    // for a few seconds based on the render framerate (60fps / playbackSpeed)
    const sample = sampleCacheAtTime(cache, time, interpolation);
    if (!sample) {
      continue;
    }
    entity.position.current.logtime = sample.logtime;
    entity.position.current.vector = sample.vector;
  }
}

/** A position cache: parallel arrays of sample times and points. */
export interface SampledCache {
  logtimes: bigint[];
  allPoints: babylon.Vector3[];
}

/**
 * The cached position at `time`: blended from the bracketing samples when
 * `interpolation` blends that pair (see {@link interpolatesBetween}), and
 * otherwise the latest sample at or before `time`.
 *
 * Clamps outside the cache's range in either mode: before the first sample
 * yields the first point, after the last yields the last.
 *
 * `logtime` is the sample's own time when one is returned as recorded (held, or
 * past the last sample), and `time` otherwise.
 *
 * **May return the cache's own vector** rather than a copy — past the last
 * sample, and at every time while holding — the historical behavior, kept
 * because it avoids an allocation on the 60fps path. Callers must therefore
 * treat the result as read-only, or use {@link localPositionAt} which always
 * copies.
 */
function sampleCacheAtTime(
  cache: SampledCache,
  time: bigint,
  interpolation: Interpolation,
): { logtime: bigint; vector: babylon.Vector3 } | undefined {
  const bracket = sampleAt(cache.logtimes, time, interpolation);
  if (bracket === undefined) {
    return undefined;
  }
  const lastPosition = cache.allPoints.at(bracket.index);
  if (lastPosition === undefined) {
    return undefined;
  }
  const nextPosition = cache.allPoints.at(bracket.index + 1);
  if (bracket.fraction === undefined || nextPosition === undefined) {
    return { logtime: bracket.sampleTime, vector: lastPosition };
  }
  return {
    logtime: time,
    vector: babylon.Vector3.Lerp(lastPosition, nextPosition, bracket.fraction),
  };
}

/**
 * The position a `Position` component held at `time`, as a fresh vector,
 * blended or held as `interpolation` says (see `sampleCacheAtTime`).
 *
 * Shared by the live per-frame update and by the resampler that bakes a global
 * trajectory, so a trace provably passes through the mesh at every sample time
 * rather than approximating it with different interpolation. Both pass the same
 * `interpolationOf(state)`, which is what keeps that true under any setting.
 *
 * Always allocates, unlike the internal fast path: the resampler accumulates
 * into its result, and mutating a `cache.allPoints` element would rewrite the
 * very history it is reading. That matters more while holding, when the fast
 * path answers with a cache element at every time rather than only past the end.
 */
export function localPositionAt(
  cache: SampledCache,
  time: bigint,
  interpolation: Interpolation,
): babylon.Vector3 | undefined {
  return sampleCacheAtTime(cache, time, interpolation)?.vector.clone();
}

/**
 * Finds the closest index of the given log time array at or before the target logtime.
 *
 * If target comes before the first logtime, returns index 0.
 * If the target comes after the last logtime, returns the last index.
 */
export function logTimeBinarySearch(arr: bigint[], target: bigint): number {
  let left = 0;
  let right = arr.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midValue = arr[mid];

    if (midValue === target) {
      return mid; // Target found
    } else if (midValue < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return Math.max(0, left - 1); // Target not found exactly, return the index before the target.
}
